import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useToken } from "@/lib/session";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Flame, Trophy, Lock, Clock, Award, Brain, Tag, CheckCircle2, XCircle,
  Bell, Palette, Megaphone, Sparkles, Zap, ArrowLeft, Upload, User, Info,
} from "lucide-react";
import { THEMES, applyTheme } from "@/lib/themes";
import { API } from "@/lib/api";
import GameWindowBanner from "@/components/GameWindowBanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import HomeHub from "@/pages/HomeHub";
import ProfileWizard from "@/components/ProfileWizard";
import WelcomePopup from "@/components/WelcomePopup";

function Countdown({ endIso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(endIso).getTime() - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div data-testid="puzzle-countdown" className="font-mono-bq font-bold text-2xl">
      {String(h).padStart(2,"0")}:{String(m).padStart(2,"0")}:{String(s).padStart(2,"0")}
    </div>
  );
}

function RebusVisual({ text }) {
  if (!text) return null;
  const colorMap = { RED: "#E62429", GREEN: "#10B981", YELLOW: "#FFD600", BLUE: "#1E88E5", BLACK: "#000", WHITE: "#FFF", PINK: "#FF69B4", GOLDEN: "#D4A017" };
  const parts = text.split(/(\[(?:RED|GREEN|YELLOW|BLUE|BLACK|WHITE|PINK|GOLDEN)\])/);
  let cur = null;
  return (
    <div className="text-4xl md:text-6xl font-black leading-tight whitespace-pre-line tracking-tight text-center text-black" data-testid="puzzle-visual">
      {parts.map((p, i) => {
        const m = p.match(/^\[(\w+)\]$/);
        if (m) { cur = colorMap[m[1]]; return null; }
        const c = cur; cur = null;
        return <span key={`${i}-${p}`} style={c ? { color: c } : {}}>{p}</span>;
      })}
    </div>
  );
}

function PuzzleCard({ item, token, onSubmitted }) {
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!answer.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/employee/submit?token=${encodeURIComponent(token)}`, {
        schedule_id: item.schedule_id, puzzle_id: item.puzzle_id, answer,
      });
      toast.success("Answer locked!");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="puzzle-card p-6 md:p-10 relative" data-testid={`puzzle-card-${item.puzzle.id}`}>
      <div className="absolute -top-5 left-6 px-3 py-1 rotate-[-3deg] brutal-shadow-sm" style={{background: "var(--bq-accent2)", border: "2px solid black"}}>
        <span className="text-xs font-black uppercase text-black">{item.puzzle.category || "Puzzle"} · {item.puzzle.difficulty}</span>
      </div>
      <div className="absolute -top-5 right-6 bg-black text-white px-3 py-1 brutal-shadow-sm">
        <Countdown endIso={item.end_time}/>
      </div>
      {item.puzzle.image_data ? (
        <img src={item.puzzle.image_data} alt="puzzle" className="w-full max-h-[420px] object-contain mt-6"/>
      ) : (
        <div className="mt-8"><RebusVisual text={item.puzzle.visual}/></div>
      )}
      {item.puzzle.hint && (
        <div className="mt-6 text-center">
          <span className="inline-flex items-center gap-2 bg-neutral-100 border border-neutral-400 px-3 py-1 text-sm font-bold text-black">
            <Tag size={14}/> Hint: {item.puzzle.hint}
          </span>
        </div>
      )}
      <div className="mt-8">
        {item.submitted ? (
          <div className="bg-neutral-100 border-4 border-black border-dashed p-6 text-center text-black" data-testid="answer-locked">
            <Lock className="inline mb-2" size={20}/>
            <div className="font-black uppercase text-lg">Answer Locked</div>
            <div className="text-sm text-neutral-600">You answered: <strong>{item.submitted_answer}</strong></div>
            <div className="text-xs mt-2 text-neutral-500">Winners revealed when timer ends.</div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <input
              data-testid={`answer-input-${item.puzzle.id}`}
              value={answer} onChange={(e)=>setAnswer(e.target.value)}
              placeholder="TYPE YOUR ANSWER..."
              className="brutal-input text-center text-2xl uppercase tracking-widest"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <button data-testid={`submit-answer-${item.puzzle.id}`} disabled={submitting || !answer.trim()} onClick={submit} className="brutal-btn">
              {submitting ? "Locking…" : "Lock My Answer"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EndedCard({ item, currentUserEmpId }) {
  return (
    <div className="brutal-card p-6 relative">
      <div className="absolute -top-4 left-4 px-3 py-1 text-xs font-black uppercase" style={{background: "var(--bq-text)", color: "var(--bq-bg)"}}>
        Reveal · {item.puzzle.category}
      </div>
      {item.puzzle.image_data ? (
        <img src={item.puzzle.image_data} alt="" className="w-full max-h-64 object-contain"/>
      ) : (
        <RebusVisual text={item.puzzle.visual}/>
      )}
      <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type:"spring", bounce:0.6 }} className="mt-6 text-center">
        <div className="font-graffiti text-5xl md:text-6xl rotate-[-2deg] inline-block" style={{ color: "var(--bq-accent)", WebkitTextStroke: "2px var(--bq-text)" }}>{item.puzzle.answer}</div>
      </motion.div>
      <div className="mt-6">
        <div className="text-xs uppercase font-black mb-2 flex items-center gap-1"><Trophy size={14}/> Top 3</div>
        {item.winners.length === 0 ? (
          <div className="text-sm" style={{color: "var(--bq-muted)"}}>No correct answers this round.</div>
        ) : (
          <div className="space-y-2">
            {item.winners.map((w) => (
              <div key={w.rank} className="flex items-center justify-between border-2 p-2 themed-border" style={{background: w.employee_id === currentUserEmpId ? "var(--bq-accent2)" : "var(--bq-card-bg)"}}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center font-black" style={{background: "var(--bq-text)", color: "var(--bq-bg)"}}>{w.rank}</div>
                  <span className="font-bold">{w.name}</span>
                </div>
                <span className="font-mono-bq text-sm">+{w.points} pts</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserAvatar({ me, size = 36 }) {
  const path = me?.avatar_path;
  const initials = (me?.name || "?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
  if (path) {
    return (
      <img
        data-testid="user-avatar-img"
        src={`${API}/files/${path}`}
        alt="avatar"
        className="border-2 themed-border object-cover"
        style={{ width: size, height: size, background: "var(--bq-card-bg)" }}
      />
    );
  }
  return (
    <div
      data-testid="user-avatar-initials"
      className="border-2 themed-border flex items-center justify-center font-black text-xs"
      style={{ width: size, height: size, background: "var(--bq-accent2)", color: "var(--bq-text)" }}
    >{initials}</div>
  );
}

function ThemePickerDialog({ open, onClose, current, onSelect, me, token, onAvatarUploaded }) {
  const [uploading, setUploading] = useState(false);
  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Avatar must be 2 MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/employee/avatar?token=${encodeURIComponent(token)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar updated");
      onAvatarUploaded(r.data.avatar_path);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="border-4 rounded-none brutal-shadow max-w-2xl themed-border themed-card">
        <DialogHeader>
          <DialogTitle className="uppercase font-black flex items-center gap-2"><Palette size={20}/> Pick Your Hero Theme</DialogTitle>
        </DialogHeader>

        {/* Avatar section */}
        <div className="border-2 themed-border p-3 flex items-center gap-3" style={{background: "var(--bq-card-bg)"}}>
          <UserAvatar me={me} size={52}/>
          <div className="flex-1 min-w-0">
            <div className="font-black uppercase text-sm flex items-center gap-1"><User size={14}/> Your Avatar</div>
            <div className="text-xs" style={{color: "var(--bq-muted)"}}>PNG / JPG / WebP · max 2 MB</div>
          </div>
          <label className="brutal-btn cursor-pointer inline-flex items-center gap-2" data-testid="avatar-upload-label">
            <Upload size={14}/> {uploading ? "Uploading…" : "Upload"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleAvatarFile}
              disabled={uploading}
              data-testid="avatar-upload-input"
            />
          </label>
        </div>

        <p className="text-sm" style={{color: "var(--bq-muted)"}}>The whole zone transforms.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
          {Object.entries(THEMES).map(([key, t]) => (
            <button
              key={key}
              data-testid={`theme-${key}`}
              onClick={() => { onSelect(key); onClose(); }}
              className={`p-4 border-2 themed-border text-left transition-all hover:scale-[1.02] ${current === key ? "themed-shadow-accent" : "themed-shadow-sm"}`}
              style={{background: t.colors.cardBg, color: t.colors.text}}
            >
              <div className="text-3xl mb-1">{t.emoji}</div>
              <div className="font-black uppercase text-sm" style={{fontFamily: t.font}}>{t.name}</div>
              <div className="text-xs mt-1" style={{color: t.colors.muted}}>{t.tagline}</div>
              <div className="flex gap-1 mt-2">
                <div className="w-4 h-4 border border-black" style={{background: t.colors.accent}}/>
                <div className="w-4 h-4 border border-black" style={{background: t.colors.accent2}}/>
                <div className="w-4 h-4 border border-black" style={{background: t.colors.bg}}/>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotifBell({ notifications, unseen, onOpen }) {
  const navigate = useNavigate();

  const handleClick = (n) => {
    // Route by notification type. Falls back to the hub.
    const dest = {
      puzzle_live: "/rebus",
      sudoku_winner: "/sudoku",
      trivia_live: "/trivia",
      caption_live: "/caption",
    }[n.type] || "/";
    navigate(dest);
  };

  return (
    <Popover onOpenChange={(o)=>o && onOpen()}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}>
          <Bell size={18}/>
          {unseen > 0 && (
            <span className="absolute -top-2 -right-2 text-white text-xs px-1.5 py-0.5 font-black border-2 border-black animate-pulse" style={{background: "var(--bq-danger)"}}>{unseen}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 border-2 themed-border themed-card rounded-none brutal-shadow-sm">
        <div className="p-3 border-b-2 themed-border font-black uppercase text-sm flex items-center gap-2"><Bell size={14}/> Notifications</div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-sm" style={{color: "var(--bq-muted)"}}>No notifications yet.</div>
          ) : notifications.map((n) => (
            <button
              key={n.id}
              type="button"
              data-testid={`notif-item-${n.id}`}
              onClick={() => handleClick(n)}
              className="block w-full text-left p-3 border-b text-sm transition-colors hover:opacity-80 cursor-pointer"
              style={{borderColor: "rgba(0,0,0,0.1)", background: "transparent"}}
            >
              <div className="font-black flex items-center gap-1">
                {n.type === "puzzle_live" ? <Sparkles size={12} className="themed-accent"/> : <Zap size={12}/>} {n.title}
              </div>
              <div style={{color: "var(--bq-muted)"}}>{n.message}</div>
              <div className="text-xs mt-1 font-mono-bq" style={{color: "var(--bq-muted)"}}>{new Date(n.created_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotifMarquee({ items }) {
  if (items.length === 0) return null;
  const looped = [...items, ...items];
  return (
    <div className="overflow-hidden border-b-2 themed-border py-2" style={{background: "var(--bq-accent2)"}}>
      <div className="notif-marquee whitespace-nowrap">
        {looped.map((n, i) => (
          <div key={`${n.id}-${i}`} className="flex items-center gap-2 px-6 text-sm font-bold text-black">
            <Megaphone size={14}/> <span>{n.title}</span> <span className="opacity-60">— {n.message}</span><span className="mx-4">·</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EmployeePage({ view }) {
  const location = useLocation();
  const token = useToken();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [info, setInfo] = useState(null);
  const [active, setActive] = useState([]);
  const [ended, setEnded] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [period, setPeriod] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [lastSeenNotif, setLastSeenNotif] = useState(localStorage.getItem("bq_last_notif") || "");
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [profileSkipped, setProfileSkipped] = useState(() => sessionStorage.getItem("bq_profile_skipped") === "1");
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  const isHub = location.pathname === "/" || location.pathname === "/play";

  const refresh = useCallback(async () => {
    try {
      const promises = [
        api.get(`/employee/me?token=${token}`),
        api.get(`/public/info`),
        api.get(`/employee/notifications?token=${token}&limit=30`),
      ];
      if (!isHub) {
        promises.push(api.get(`/employee/active?token=${token}`));
        promises.push(api.get(`/employee/ended?token=${token}&limit=3`));
      }
      const results = await Promise.all(promises);
      setMe(results[0].data); setInfo(results[1].data); const notR = results[2];
      if (!isHub) { setActive(results[3].data); setEnded(results[4].data); }
      setError(null);
      if (!firstLoad.current) {
        for (const n of notR.data) {
          if (!seenIds.current.has(n.id)) toast(n.title, { description: n.message, icon: <Sparkles size={16}/> });
        }
      }
      notR.data.forEach((n) => seenIds.current.add(n.id));
      setNotifications(notR.data);
      if (firstLoad.current) { applyTheme(results[0].data.theme || "standard"); firstLoad.current = false; }
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load. Check your token.");
    } finally { setLoading(false); }
  }, [token, isHub]);

  const fetchLb = useCallback(async (p) => {
    try { const r = await api.get(`/employee/leaderboard?token=${token}&period=${p}`); setLeaderboard(r.data); }
    catch (e) { console.error("leaderboard load failed", e); }
  }, [token]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => { if (token && !error && !isHub) fetchLb(period); }, [period, token, error, isHub, fetchLb]);

  const changeTheme = async (themeKey) => {
    applyTheme(themeKey);
    try { await api.put(`/employee/theme?token=${token}`, { theme: themeKey }); toast.success(`Theme: ${THEMES[themeKey].name}`); }
    catch (e) { console.error("theme save failed", e); toast.error("Couldn't save theme"); }
  };

  const markNotifsSeen = () => {
    if (notifications[0]) { localStorage.setItem("bq_last_notif", notifications[0].id); setLastSeenNotif(notifications[0].id); }
    // Server-side read cursor (so /employee/me.unread_notifications stays accurate
    // across devices and after a localStorage wipe). Fire-and-forget.
    api.post(`/employee/notifications/mark-read?token=${token}`).catch(()=>{});
  };
  const unseenCount = (() => {
    if (!notifications.length) return 0;
    const idx = notifications.findIndex((n) => n.id === lastSeenNotif);
    return idx === -1 ? notifications.length : idx;
  })();

  if (loading) return <div className="min-h-screen flex items-center justify-center font-black text-2xl">Loading…</div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="brutal-card p-8 max-w-md text-center">
        <XCircle className="mx-auto mb-3" size={48} style={{color: "var(--bq-danger)"}}/>
        <h1 className="text-2xl font-black uppercase">Access Denied</h1>
        <p className="mt-2" style={{color: "var(--bq-muted)"}}>{error}</p>
      </div>
    </div>
  );

  // On the hub, HomeHub renders its own larger user chip with avatar.
  // On other pages, include a small avatar inline with the bell/palette.
  const headerExtras = (
    <>
      {!isHub && <UserAvatar me={me} size={36}/>}
      <NotifBell notifications={notifications} unseen={unseenCount} onOpen={markNotifsSeen}/>
      <button data-testid="points-link" onClick={()=>navigate("/points")} title="My Points Log" className="p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}>
        <Trophy size={18}/>
      </button>
      <button data-testid="about-link" onClick={()=>navigate("/about")} title="About & Rules" className="p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}>
        <Info size={18}/>
      </button>
      <button data-testid="theme-toggle" onClick={()=>setThemePickerOpen(true)} className="p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}>
        <Palette size={18}/>
      </button>
    </>
  );

  const handleAvatarUploaded = (path) => setMe((cur) => cur ? { ...cur, avatar_path: path } : cur);

  // First-time profile form (shown on the hub only). Demo users skip it permanently
  // so previewers aren't blocked by the questionnaire.
  const needsProfile = isHub && me && !me.is_demo && !me.profile_completed && !profileSkipped;

  const onProfileCompleted = () => {
    // Re-fetch so we get fun_title + profile_completed = true.
    refresh();
    setWelcomeOpen(true);
  };
  const onProfileSkip = () => {
    sessionStorage.setItem("bq_profile_skipped", "1");
    setProfileSkipped(true);
  };

  const onWelcomeAction = (key) => {
    setWelcomeOpen(false);
    if (key === "tour") { localStorage.setItem("bq_show_tour", "1"); }
    if (key === "theme") setThemePickerOpen(true);
    if (key === "avatar") setThemePickerOpen(true); // ThemePicker hosts the avatar uploader
    // "play" — just close, hub is already visible.
  };

  if (isHub) {
    return (
      <>
        <NotifMarquee items={notifications.slice(0,5)}/>
        <HomeHub me={me} info={info} token={token} headerExtras={headerExtras}/>
        <ThemePickerDialog open={themePickerOpen} onClose={()=>setThemePickerOpen(false)} current={me?.theme} onSelect={changeTheme} me={me} token={token} onAvatarUploaded={handleAvatarUploaded}/>
        {needsProfile && (
          <ProfileWizard me={me} token={token} onCompleted={onProfileCompleted} onSkip={onProfileSkip}/>
        )}
        <WelcomePopup
          open={welcomeOpen}
          onClose={()=>setWelcomeOpen(false)}
          onAction={onWelcomeAction}
          displayName={me?.name}
          funTitle={me?.fun_title}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen themed-bg">
      <NotifMarquee items={notifications.slice(0,5)}/>
      <header className="border-b-4 px-6 py-4 flex flex-wrap items-center justify-between gap-3 themed-border" style={{background: "var(--bq-header-bg)"}}>
        <div className="flex items-center gap-3">
          <button onClick={()=>navigate(-1)} data-testid="back-to-hub" className="p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}><ArrowLeft size={18}/></button>
          <div>
            <div className="font-black uppercase tracking-tight themed">Rebus · Daily</div>
            <div className="text-xs font-mono-bq" style={{color: "var(--bq-muted)"}}>{me?.name}</div>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap" data-testid="user-stats">
          <div className="border-2 themed-border px-3 py-1 brutal-shadow-sm flex items-center gap-1" style={{background: "var(--bq-accent2)", color: "var(--bq-text)"}}>
            <Flame size={16}/><span className="font-black">{me?.streak}</span><span className="text-xs">streak</span>
          </div>
          <div className="brutal-card px-3 py-1 brutal-shadow-sm flex items-center gap-1">
            <Award size={16}/><span className="font-black">{me?.points}</span><span className="text-xs">pts</span>
          </div>
          {headerExtras}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-3"><GameWindowBanner game="rebus" /></div>
        <div className="lg:col-span-2 space-y-12">
          {active.length === 0 ? (
            <div className="brutal-card p-10 text-center">
              <Clock className="mx-auto mb-3" size={42}/>
              <h2 className="text-3xl font-black uppercase">No puzzle live right now</h2>
              <p className="mt-2" style={{color: "var(--bq-muted)"}}>Come back during the daily window. Admin pushes 1–3 puzzles per day.</p>
            </div>
          ) : active.map((it) => (
            <PuzzleCard key={`${it.schedule_id}-${it.puzzle.id}`} item={it} token={token} onSubmitted={refresh}/>
          ))}
          {ended.length > 0 && (
            <div>
              <h2 className="text-3xl font-black uppercase mb-4 flex items-center gap-2"><CheckCircle2 size={28}/> Recent Reveals</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {ended.map((it) => <EndedCard key={it.puzzle.id} item={it} currentUserEmpId={me?.employee_id}/>)}
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="brutal-card p-5 sticky top-6">
            <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-black uppercase flex items-center gap-2"><Trophy size={20}/> Leaderboard</h3></div>
            <div className="flex gap-1 mb-3 border-2 themed-border p-1">
              {["daily","weekly","monthly","all"].map((p) => (
                <button key={p} data-testid={`leaderboard-tab-${p}`} onClick={()=>setPeriod(p)} className="flex-1 text-xs font-black uppercase py-1" style={{background: period === p ? "var(--bq-text)" : "var(--bq-card-bg)", color: period === p ? "var(--bq-bg)" : "var(--bq-text)"}}>{p}</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {leaderboard.length === 0 && <div className="text-sm text-center py-4" style={{color: "var(--bq-muted)"}}>No data yet.</div>}
              {leaderboard.map((row, i) => (
                <div key={row.user_id} className="flex items-center justify-between p-2 border-2 themed-border" style={{background: row.employee_id === me?.employee_id ? "var(--bq-accent2)" : "var(--bq-card-bg)"}}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center font-black text-sm" style={{background: i === 0 ? "#FFD600" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "var(--bq-text)", color: i < 3 ? "#000" : "var(--bq-bg)"}}>{i+1}</div>
                    <div>
                      <div className="font-bold text-sm">{row.name}</div>
                      <div className="text-xs font-mono-bq" style={{color: "var(--bq-muted)"}}>{row.wins} wins</div>
                    </div>
                  </div>
                  <div className="font-mono-bq font-black">{row.points}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>

      <ThemePickerDialog open={themePickerOpen} onClose={()=>setThemePickerOpen(false)} current={me?.theme} onSelect={changeTheme} me={me} token={token} onAvatarUploaded={handleAvatarUploaded}/>
    </div>
  );
}
